// ============================================
// ACCEPT INVITE PAGE — public route
// /accept-invite/:token
// ============================================

import React, { useState, useEffect } from "react";
import { useTheme } from "../components/ThemeContext";
import { apiVerifyInvite, apiAcceptInvite } from "../services/api";

export default function AcceptInvite({ token }) {
  const { C } = useTheme();
  const [loading, setLoading] = useState(true);
  const [invited, setInvited] = useState(null);   // { email, role }
  const [error, setError]     = useState(null);
  const [name, setName]       = useState("");
  const [pw, setPw]           = useState("");
  const [pw2, setPw2]         = useState("");
  const [busy, setBusy]       = useState(false);

  useEffect(() => {
    apiVerifyInvite(token)
      .then(r => setInvited(r.data))
      .catch(err => setError(err.response?.data?.message || "Invitation invalid"))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (name.trim().length < 2) return setError("Please enter your name");
    if (pw.length < 8)           return setError("Password must be at least 8 characters");
    if (pw !== pw2)              return setError("Passwords don't match");

    setBusy(true);
    try {
      const r = await apiAcceptInvite(token, name.trim(), pw);
      // Auto-login: store token and reload to enter the app
      localStorage.setItem("vh_token", r.data.token);
      window.location.replace("/");
    } catch(err) {
      setError(err.response?.data?.message || "Failed to accept invitation");
      setBusy(false);
    }
  };

  const inp = { width:"100%", padding:"11px 14px", background:C.inputBg, border:`1px solid ${C.border}`, borderRadius:9, color:C.text, fontSize:14, outline:"none", boxSizing:"border-box" };

  if (loading) {
    return (
      <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:C.bg, color:C.muted }}>
        Verifying invitation…
      </div>
    );
  }

  if (!invited) {
    return (
      <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", padding:"24px 16px", background:C.bg }}>
        <div style={{ width:"100%", maxWidth:420, textAlign:"center" }}>
          <div style={{ fontSize:48, marginBottom:14 }}>⚠️</div>
          <div style={{ fontSize:18, fontWeight:800, color:C.text, marginBottom:8 }}>Invitation problem</div>
          <div style={{ fontSize:13, color:C.muted, marginBottom:24 }}>{error}</div>
          <a href="/" style={{ color:C.accent, fontSize:13, fontWeight:700, textDecoration:"none" }}>← Go to sign in</a>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", padding:"24px 16px", background:C.bg }}>
      <div style={{ width:"100%", maxWidth:440 }}>
        <div style={{ textAlign:"center", marginBottom:32 }}>
          <div style={{ fontSize:30, fontWeight:800, color:C.text, fontFamily:"Georgia, serif", marginBottom:6 }}>VolunteerHub</div>
          <div style={{ fontSize:13, color:C.muted }}>Welcome — set up your account</div>
        </div>

        <form onSubmit={submit} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, padding:"28px 26px" }}>
          <div style={{ background:C.greenSoft, border:`1px solid ${C.green}44`, borderRadius:9, padding:"10px 12px", marginBottom:18, fontSize:12, color:C.green, fontWeight:600 }}>
            ✓ You're invited as <strong>{invited.email}</strong> ({String(invited.role).replace("_"," ")})
          </div>

          {error && (
            <div style={{ padding:"10px 14px", borderRadius:8, marginBottom:14, background:C.redSoft, border:`1px solid ${C.red}44`, color:C.red, fontSize:13, fontWeight:600 }}>
              {error}
            </div>
          )}

          <div style={{ marginBottom:14 }}>
            <div style={{ fontSize:11, fontWeight:600, color:C.muted, marginBottom:6, textTransform:"uppercase", letterSpacing:"0.5px" }}>Your name</div>
            <input autoFocus value={name} onChange={e=>setName(e.target.value)} placeholder="Full name" style={inp} required />
          </div>

          <div style={{ marginBottom:14 }}>
            <div style={{ fontSize:11, fontWeight:600, color:C.muted, marginBottom:6, textTransform:"uppercase", letterSpacing:"0.5px" }}>Password</div>
            <input type="password" value={pw} onChange={e=>setPw(e.target.value)} placeholder="At least 8 characters" style={inp} required />
          </div>

          <div style={{ marginBottom:18 }}>
            <div style={{ fontSize:11, fontWeight:600, color:C.muted, marginBottom:6, textTransform:"uppercase", letterSpacing:"0.5px" }}>Confirm password</div>
            <input type="password" value={pw2} onChange={e=>setPw2(e.target.value)} placeholder="Re-enter password" style={inp} required />
          </div>

          <button type="submit" disabled={busy}
            style={{ width:"100%", padding:"12px", background:busy?C.border:C.accent, border:"none", borderRadius:9, color:busy?C.muted:"#000", fontSize:14, fontWeight:700, cursor:busy?"not-allowed":"pointer" }}>
            {busy ? "Setting up…" : "Activate account"}
          </button>
        </form>
      </div>
    </div>
  );
}